import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgbDate, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';
import type { SwiperOptions } from 'swiper/types/swiper-options';
import { FtSwiperDirective } from 'apps/account/src/account-shared/modules/swiper/ft-swiper.directive';
import { CalendarBase } from '../classes/calendar-base';
import { getRangeDate } from '../utils';
import { DateUtils } from '@flipto/shared/src/lib/utils/common/time/date-utils';
import {
    SelectableDateType
} from 'apps/account/src/account-shared/modules/monthly-daterange-picker/enums/selecable-date-type.enum';
import {
    AVAILABLE_DAY_RANGES,
    DATEPICKER_FIXED_RANGE_DAYS,
    DATEPICKER_MAX_DATE,
    DATEPICKER_MIN_DATE,
    DATEPICKER_TEXTS,
    MonthlyDateRangePickerTexts,
} from 'apps/account/src/account-shared';
import { SelectMode } from 'apps/account/src/account-shared/modules/monthly-daterange-picker/enums/select-mode.enum';
import dayjs from 'dayjs/esm/index';
import isBetween from 'dayjs/esm/plugin/isBetween';

dayjs.extend(isBetween);

@Component({
    selector: 'ft-monthly-daterange-picker',
    templateUrl: './monthly-daterange-picker.component.html',
    styleUrls: ['./monthly-daterange-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('expandCollapse', [
            state('void', style({ height: '0', opacity: 0, overflow: 'hidden' })),
            state('*', style({ height: '*', opacity: 1, overflow: 'hidden' })),
            transition('void <=> *', [
                animate('500ms cubic-bezier(0.7, 0, 0.1, 1)')
            ])
        ])
    ],
    standalone: false
})
export class MonthlyDaterangePickerComponent extends CalendarBase implements OnInit, AfterViewInit, OnDestroy {
    readonly selectModeEnum = SelectMode;
    @Output() changed = new EventEmitter<{ startDate: Date, endDate: Date }>();
    /** Total months rendered — computed in initCalendar, grows as past months are prepended */
    displayMonths = 24;
    /** Months initially rendered before the start date. The past is unlimited: another
     *  chunk is prepended whenever the user scrolls close to the oldest rendered month. */
    @Input() pastMonths = 24;
    /** Months rendered after the initial date */
    @Input() futureMonths = 12;
    @Input() fromDate: Date;
    @Input() toDate: Date;
    @Input() theme: 'dark' | 'light' | 'gray' = 'dark';
    /** A live override for editors whose required range length can change after injection. */
    @Input() set fixedRangeDaysOverride(days: number | undefined) {
        if (days != null) this.fixedRangeDays = days;
    }
    @ViewChild('swiper', { static: false, read: FtSwiperDirective }) swiper?: FtSwiperDirective;
    @ViewChild('dp') datepicker: NgbDatepicker;
    @ViewChildren('calendarMonth') calendarMonths: QueryList<ElementRef>;
    initialCalendarDate: NgbDate;
    // ngb >=17 [weekdays] takes an Intl weekday string, not Angular's numeric
    // TranslationWidth enum (which threw "Value 1 out of range" in Intl).
    weekdaysWidth: Exclude<Intl.DateTimeFormatOptions['weekday'], undefined> = 'short';
    currentYear = new Date().getFullYear();
    targetSlideIndex: number;
    isNextButtonDisabled = true;
    isPrevButtonDisabled = true;
    preloadedMonths = 12;
    preloadedDatesRange: Date[];
    selectMode = this.availableDayRanges?.length ? SelectMode.Range : SelectMode.Day;

    timestamp = new Date().getTime();
    monthAnimation = true;
    hoveredMonth: Date = null;
    isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    allowClick = true;

    /** When the active slide gets this close to the oldest rendered month, prepend more */
    private readonly prependThreshold = 6;
    /** How many past months get prepended per extension */
    private readonly prependChunk = 24;
    private isPrepending = false;

    /**
     * False while WE are the ones moving the carousel — from creation until the opening position
     * has been applied, and again across a re-render or a past-month prepend. Slide events that
     * arrive in those windows are swiper's own positioning, not the user navigating.
     */
    private initialSyncDone = false;

    get swiperConfig(): SwiperOptions {
        return {
            roundLengths: true,
            simulateTouch: true,
            freeMode: { enabled: true, momentum: true, momentumRatio: 0.7, momentumBounce: false },
            // Trackpad / horizontal wheel scrolling; forceToAxis keeps vertical wheel on the page
            mousewheel: { forceToAxis: true, sensitivity: 1, enabled: true },
            slidesPerView: 'auto',
            initialSlide: this.targetSlideIndex,
            centeredSlides: true,
        };
    }

    constructor(@Optional() @Inject(DATEPICKER_MAX_DATE) public maxDate: Date,
                @Optional() @Inject(DATEPICKER_MIN_DATE) public minDate: Date,
                @Optional() @Inject(AVAILABLE_DAY_RANGES) public availableDayRanges: number[],
                @Optional() @Inject(DATEPICKER_TEXTS) public texts: MonthlyDateRangePickerTexts,
                @Optional() @Inject(DATEPICKER_FIXED_RANGE_DAYS) public fixedRangeDays: number | null,
                cdr: ChangeDetectorRef) {
        super(cdr);
        this.selectSingle = !!this.availableDayRanges?.length;

        // Set protected properties for base class availability checks
        this.maxDate = maxDate;
        this.minDate = minDate;
    }

    changeDetection() {
        this.cdr.detectChanges();
    }

    reRender() {
        // Capture the live scroll position before the swiper is torn down. The
        // swiperslidechangetransitionend-driven value in targetSlideIndex can be
        // stale (freeMode momentum still settling, or a click mid-scroll), so read
        // it straight off the live instance. Index 0 is a valid slide → != null guard.
        const liveIndex = this.swiper?.swiper?.activeIndex;
        if (liveIndex != null) {
            this.targetSlideIndex = liveIndex;
        }

        this.timestamp = null;
        this.selectedMonths = [];
        // A fresh swiper is about to position itself from scratch — ignore its slide events
        // until syncToTarget has put it where we want it.
        this.initialSyncDone = false;

        this.changeDetection();
        this.timestamp = new Date().getTime();
        this.changeDetection(); // recreate datepicker → swiper at initialSlide

        // initialSlide + centeredSlides/'auto' only re-centers approximately and can
        // land 1-2 slides off; re-sync explicitly once the rebuilt slides have laid
        // out. runCallbacks=false so this doesn't re-enter onSlideChange.
        setTimeout(() => this.syncToTarget());
    }

    /**
     * Put the carousel on {@link targetSlideIndex} once the surrounding layout has settled, and
     * re-arm {@link onSlideChange}.
     *
     * runCallbacks=false so this doesn't re-enter onSlideChange.
     */
    private syncToTarget() {
        const swiper = this.swiper?.swiper;
        swiper?.update();
        swiper?.slideTo(this.targetSlideIndex, 0, false);
        // No instance yet means nothing was positioned — don't arm the guard against a move we never made.
        this.initialSyncDone = !!swiper;
        this.setArrowsDisability();
    }

    ngOnInit(): void {
        this.initCalendar();
    }

    ngAfterViewInit() {
        this.focusCalendar();

        // Swiper initializes while the host dialog is still laying out, so
        // initialSlide + centeredSlides/'auto' can land off the intended month —
        // the same failure reRender guards against. Re-sync once layout settles.
        setTimeout(() => this.syncToTarget());

        // Disable month animation in order to not see animation each time when we toggle mode
        setTimeout(() => {
            this.monthAnimation = false;
        }, 700);
    }

    ngOnDestroy() {
    }

    /**
     * Adopt the user's scrolling as the new target — and only the user's.
     *
     * Swiper emits this event on init too, and init measures a container the host dialog is still
     * laying out: at width 0 `updateSize` bails, `slidesGrid` is degenerate, and `slideTo` snaps
     * the reported index to ~0. Adopting that was FTK-7640 — the 0 overwrote the target,
     * `maybePrependPastMonths` read it as scrolling into the past, and the picker opened
     * `prependChunk` months behind today. So drop every event fired while we are the ones moving
     * the carousel; `syncToTarget` re-arms once it is where we want it.
     */
    onSlideChange(e: CustomEvent) {
        const swiperInstance = (e as any).detail?.[0];
        const activeIndex: number | undefined = swiperInstance?.activeIndex;
        // Wheel scrolling (freeMode) only emits 'scroll', never slideChangeTransitionEnd, so
        // the same handler is bound to both; skip the no-op ticks where the slide didn't change.
        if (activeIndex == null || !this.initialSyncDone || activeIndex === this.targetSlideIndex) {
            return;
        }

        this.targetSlideIndex = activeIndex;
        const centerMonth = DateUtils.add(DateUtils.ngbDateToDate(this.initialCalendarDate), this.targetSlideIndex - 1, 'month').toDate();
        this.preloadedDatesRange = [DateUtils.subtract(centerMonth, this.preloadedMonths, 'month').toDate(), DateUtils.add(centerMonth, this.preloadedMonths, 'month').toDate()];
        this.maybePrependPastMonths();
    }

    /**
     * The past is unlimited: when the user scrolls close to the oldest rendered month,
     * shift the calendar start another chunk back and re-sync the swiper to the same
     * visual position. Months outside the preloaded window stay empty placeholders,
     * so the slide count can keep growing cheaply.
     */
    private maybePrependPastMonths() {
        if (this.isPrepending || this.targetSlideIndex > this.prependThreshold) {
            return;
        }
        this.isPrepending = true;
        // Prepending renumbers every slide, so swiper is about to re-position itself around the
        // new slide count — its events are ours, not the user's, until syncToTarget runs.
        this.initialSyncDone = false;

        this.initialCalendarDate = DateUtils.dateToNgbDate(
            DateUtils.subtract(DateUtils.ngbDateToDate(this.initialCalendarDate), this.prependChunk, 'month').toDate()
        );
        this.displayMonths += this.prependChunk;
        this.targetSlideIndex += this.prependChunk;
        this.changeDetection(); // regenerate datepicker months → swiper slides

        // Re-sync the swiper after the prepended slides have rendered
        setTimeout(() => {
            const centerMonth = DateUtils.add(DateUtils.ngbDateToDate(this.initialCalendarDate), this.targetSlideIndex - 1, 'month').toDate();
            this.preloadedDatesRange = [DateUtils.subtract(centerMonth, this.preloadedMonths, 'month').toDate(), DateUtils.add(centerMonth, this.preloadedMonths, 'month').toDate()];
            this.syncToTarget();
            this.isPrepending = false;
        });
    }

    onTouchStart() {
        this.allowClick = false;
    }

    onTouchEnd() {
        this.allowClick = true;
    }

    onSlideClick(e) {
        if (!this.allowClick) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    toggleMode() {
        this.selectMode = this.selectMode === SelectMode.Range ? SelectMode.Day : SelectMode.Range;
        this.reRender();
    }

    setMode(mode: SelectMode) {
        if (this.selectMode !== mode) {
            this.selectMode = mode;
            this.reRender();
        }
        // to be used in template logic
        return true;
    }


    isMonthSelected(date: Date | NgbDate) {
        const currentMonth = this.toDateFormat(date);

        return this.selectedMonths.some(month => dayjs(month).isSame(dayjs(currentMonth), 'month'))
    }

    isDateInView(date: NgbDate): boolean {
        if (this.preloadedDatesRange?.length) {
            return DateUtils.isBetween(this.preloadedDatesRange[0], this.preloadedDatesRange[1], DateUtils.ngbDateToDate(date));
        }
        return true;
    }

    initCalendar() {
        const centerDate = this.fromDate || new Date();
        this.displayMonths = this.pastMonths + this.futureMonths;
        this.initialCalendarDate = DateUtils.dateToNgbDate(DateUtils.subtract(centerDate, this.pastMonths, 'month').toDate());
        this.focusDate = centerDate;
        this.targetSlideIndex = this.pastMonths;
        // Virtualize day grids from the very first paint — without an initial preload
        // window every rendered month would build its full day grid at once.
        this.preloadedDatesRange = [
            DateUtils.subtract(centerDate, this.preloadedMonths, 'month').toDate(),
            DateUtils.add(centerDate, this.preloadedMonths, 'month').toDate()
        ];

        if (this.fixedRangeDays) {
            this.markDisabled = (ngbDate: NgbDate) => {
                const date = DateUtils.ngbDateToDate(ngbDate);
                const rangeEnd = dayjs(date).add(this.fixedRangeDays - 1, 'day');
                if (this.maxDate && rangeEnd.isAfter(dayjs(this.maxDate), 'day')) {
                    return true;
                }
                if (this.minDate && dayjs(date).isBefore(dayjs(this.minDate), 'day')) {
                    return true;
                }
                return false;
            };
            return;
        }

        // Wrap to timeout in order to avoid stucked disabled state on days
        setTimeout(() => {
            if (this.fromDate) {
                this.startDate = this.fromDate;
            }
            if (this.toDate) {
                this.endDate = this.toDate;
            }
        });
    }

    isInactiveDate = (ngbDate: NgbDate): boolean => {
        if (!this.fixedRangeDays) return false;
        const date = DateUtils.ngbDateToDate(ngbDate);
        if (this.maxDate && dayjs(date).isAfter(dayjs(this.maxDate), 'day')) return false;
        if (this.minDate && dayjs(date).isBefore(dayjs(this.minDate), 'day')) return false;
        const rangeEnd = dayjs(date).add(this.fixedRangeDays - 1, 'day');
        return !!this.maxDate && rangeEnd.isAfter(dayjs(this.maxDate), 'day');
    };

    setArrowsDisability() {
        const activeIndex = this.swiper?.swiper?.activeIndex ?? 0;
        const slideCount = this.swiper?.swiper?.slides?.length ?? 0;
        // The past is unlimited (months are prepended on demand) — prev never disables
        this.isPrevButtonDisabled = false;
        if (window.matchMedia(`(min-width: 1800px)`).matches) {
            this.isNextButtonDisabled = activeIndex >= slideCount - 4;
        } else if (window.matchMedia(`(min-width: 1100px)`).matches) {
            this.isNextButtonDisabled = activeIndex >= slideCount - 3;
        } else {
            this.isNextButtonDisabled = activeIndex >= slideCount - 1;
        }
        this.cdr.detectChanges();
    }

    slideNext() {
        this.swiper.slideNext(300);
        this.setArrowsDisability();
    }

    slidePrev() {
        this.swiper.slidePrev(300);
        this.setArrowsDisability();
    }

    focusCalendar() {
        this.datepicker.focus();
        if (this.focusDate) {
            this.datepicker.focusDate(DateUtils.dateToNgbDate(this.focusDate));
        }
    }

    datepickerFocus() {
        this.datepicker.focus();
        this.changeDetection();
    }

    toggleMonthSelection(date: Date | NgbDate) {
        const startOfMonth = dayjs(this.toDateFormat(date)).startOf('month');
        const monthDate = startOfMonth.toDate();

        if (this.isMonthAvailable(monthDate)) {

            this.selectedMonths.push(monthDate);
            this.selectedMonths.sort((a, b) => a.getTime() - b.getTime());

            this.backfillMissingMonths();

            if (!!this.startDate) {
                this.setSelectedMonthsRange()
            }

            if (!this.startDate) {
                this.setStartDate(dayjs(this.selectedMonths[0]).startOf('month').toDate());
            }
        }
        this.selectedMonths = [...this.selectedMonths];

        this.cdr.detectChanges();
    }

    backfillMissingMonths() {
        if (this.selectedMonths.length < 2) {
            return;
        }

        const filledMonths: Date[] = [];

        for (let i = 0; i < this.selectedMonths.length; i++) {
            filledMonths.push(this.selectedMonths[i]);

            if (i < this.selectedMonths.length - 1) {
                const currentMonth = dayjs(this.selectedMonths[i]);
                const nextMonth = dayjs(this.selectedMonths[i + 1]);
                const monthsDiff = nextMonth.diff(currentMonth, 'month');

                if (monthsDiff > 1) {
                    let monthToAdd = currentMonth.add(1, 'month');
                    while (monthToAdd.isBefore(nextMonth, 'month')) {
                        if (this.isMonthAvailable(monthToAdd.toDate())) {
                            filledMonths.push(monthToAdd.toDate());
                        }
                        monthToAdd = monthToAdd.add(1, 'month');
                    }
                }
            }
        }

        this.selectedMonths = filledMonths.sort((a, b) => a.getTime() - b.getTime());
    }

    isMonthAvailable(date: Date | NgbDate): boolean {
        const month = this.toDateFormat(date);
        const monthStart = dayjs(month).startOf('month');
        const monthEnd = dayjs(month).endOf('month');
        const today = dayjs().startOf('day');

        if (this.minDate && monthEnd.isBefore(dayjs(this.minDate).startOf('day'), 'day')) {
            return false;
        }

        if (this.maxDate && monthStart.isAfter(dayjs(this.maxDate).endOf('day'), 'day')) {
            return false;
        }

        if (this.startDate) {
            return !monthStart.isBefore(dayjs(this.startDate).startOf('month')) && !monthStart.isAfter(today, 'day');
        }

        return !monthStart.isAfter(today, 'day');
    }

    isMonthInRange(date: Date | NgbDate): boolean {
        const month = this.toDateFormat(date);

        if (!this.startDate || !this.hoveredDate) {
            return false;
        }

        return dayjs(month).isBetween(dayjs(this.startDate), dayjs(this.hoveredDate), 'month', '[]');
    }

    isStartDateNextMonthAvailable(): boolean {
        if (!this.startDate)
            return false;
        return this.isMonthAvailable(dayjs(this.startDate).add(1, 'month').toDate());
    }

    setSelectedMonthsRange() {
        if (this.selectedMonths.length) {
            const firstMonth = this.selectedMonths[0];
            const lastMonth = this.selectedMonths[this.selectedMonths.length - 1];

            const firstAvailableDate = this.getFirstAvailableDateInMonth(firstMonth);
            const lastAvailableDate = this.getLastAvailableDateInMonth(lastMonth);

            if (firstAvailableDate) {
                this.setStartDate(firstAvailableDate);
            }
            if (lastAvailableDate) {
                this.setEndDate(lastAvailableDate);
            }
        }
    }

    private getFirstAvailableDateInMonth(month: Date): Date | null {
        const monthStart = dayjs(month).startOf('month');
        const monthEnd = dayjs(month).endOf('month');

        let firstAvailable = monthStart;

        if (this.minDate) {
            const minDateDayjs = dayjs(this.minDate).startOf('day');
            if (minDateDayjs.isAfter(monthEnd, 'day')) {
                return null;
            }
            if (minDateDayjs.isAfter(firstAvailable, 'day') && (minDateDayjs.isBefore(monthEnd, 'day') || minDateDayjs.isSame(monthEnd, 'day'))) {
                firstAvailable = minDateDayjs;
            }
        }


        if (firstAvailable.isAfter(monthEnd, 'day')) {
            return null;
        }

        return firstAvailable.toDate();
    }

    private getLastAvailableDateInMonth(month: Date): Date | null {
        const monthStart = dayjs(month).startOf('month');
        const monthEnd = dayjs(month).endOf('month');

        let lastAvailable = monthEnd;

        if (this.maxDate) {
            const maxDateDayjs = dayjs(this.maxDate).startOf('day');

            if (maxDateDayjs.isBefore(monthStart, 'day')) {
                return null;
            }

            if ((maxDateDayjs.isAfter(monthStart, 'day') || maxDateDayjs.isSame(monthStart, 'day')) &&
                (maxDateDayjs.isBefore(monthEnd, 'day') || maxDateDayjs.isSame(monthEnd, 'day'))) {
                lastAvailable = maxDateDayjs;
            }
        }

        if (lastAvailable.isBefore(monthStart, 'day')) {
            return null;
        }

        return lastAvailable.toDate();
    }

    onDateSelection(date: Date | NgbDate) {
        if (this.fixedRangeDays) {
            const d = this.toDateFormat(date);
            this.startDate = d;
            this.endDate = dayjs(d).add(this.fixedRangeDays - 1, 'day').toDate();
            this.onDatesSelected();
            return;
        }
        if (this.snapsToHintedRange) {
            // Selecting a date inside an available range snaps to that range's end (e.g. picking a day
            // inside the 14-day window selects 14 days). Clicking past the last available range end is
            // out of bounds — ignore it so an unintended range isn't applied behind the cursor.
            const snappedEnd = this.nextHintedEnd(this.toDateFormat(date));
            if (!snappedEnd) {
                return;
            }
            super.onDateSelection(snappedEnd);
            return;
        }
        super.onDateSelection(date);
    }

    /** Calendar-cell hover. In range step 2 the selection box snaps to the nearest valid range end. */
    onDayHover(date: Date | NgbDate) {
        const d = this.toDateFormat(date);
        this.hoveredDate = this.snapsToHintedRange ? this.snapToHintedEnd(d) : d;
        this.changeDetection();
    }

    /** True while picking the end in range mode with quick-pick ranges available. */
    private get snapsToHintedRange(): boolean {
        return this.selectMode === SelectMode.Range
            && !!this.availableDayRanges?.length
            && !!this.startDate && !this.endDate;
    }

    /** Valid range-end dates for the current start (within maxDate), ascending. */
    private getHintedEndDates(): Date[] {
        if (!this.startDate || !this.availableDayRanges?.length) {
            return [];
        }
        return this.availableDayRanges
            .map(range => getRangeDate(this.startDate, range))
            .filter(date => !this.isRangeDisabled(date))
            .sort((a, b) => a.getTime() - b.getTime());
    }

    /** First valid range end on or after `date`, or null when `date` is past the last available range end. */
    private nextHintedEnd(date: Date): Date | null {
        return this.getHintedEndDates().find(h => !dayjs(h).isBefore(dayjs(date), 'day')) ?? null;
    }

    /** Nearest valid range end >= date; capped at the last available range end when date is beyond it.
     *  Used for hover so the selection box stops at (not beyond) the last available range. */
    private snapToHintedEnd(date: Date): Date {
        const hinted = this.getHintedEndDates();
        if (!hinted.length) {
            return date;
        }
        return this.nextHintedEnd(date) ?? hinted[hinted.length - 1];
    }

    onDatesSelected() {
        this.changed.next({
            startDate: this.startDate,
            endDate: this.endDate
        });
    }

    onMonthHover(month: NgbDate | Date) {
        this.hoveredMonth = dayjs(this.toDateFormat(month)).endOf('month').toDate();
        this.changeDetection();
    }

    isRangeDisabled(date: Date) {
        return this.maxDate && DateUtils.isAfter(date, this.maxDate);
    }

    get selectableDateType() {
        if (this.startDate && this.endDate) {
            return SelectableDateType.Start;
        }
        return this.startDate && !this.endDate ? SelectableDateType.End : SelectableDateType.Start;
    }

    get selectedMonthsDaysTotalCount(): number {
        if (!this.selectedMonths || this.selectedMonths.length === 0) {
            return 0;
        }

        return this.selectedMonths.reduce((acc, month) => {
            const firstAvailableDate = this.getFirstAvailableDateInMonth(month);
            const lastAvailableDate = this.getLastAvailableDateInMonth(month);

            if (!firstAvailableDate || !lastAvailableDate) {
                return acc;
            }

            const firstDate = dayjs(firstAvailableDate).startOf('day');
            const lastDate = dayjs(lastAvailableDate).startOf('day');

            if (firstDate.isAfter(lastDate, 'day')) {
                return acc;
            }

            const daysInMonth = lastDate.diff(firstDate, 'day') + 1;
            return acc + daysInMonth;
        }, 0);
    }

    reset(event?: Event) {
        super.reset(event);

        this.hoveredMonth = null;
        // Restore the default end-selection mode instead of toggling it — flipping
        // here caused Range/Day to alternate each time the start date was changed.
        this.selectMode = this.availableDayRanges?.length ? SelectMode.Range : SelectMode.Day;
        this.reRender();
    }


}
